


























// This component is typically used as a dynamic component inside ModalWrapperComponent

import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { StoryUser } from '../StoryUser';
import * as utils from '../utils';
import * as utils_auth from '../utils_auth';
import { RawLocation } from 'vue-router';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';
import { globalUC } from '../UserCache';

@Component({
  name: 'RegisterFormComponent',
  components: { 
    VuePhoneNumberInput: () => import('vue-phone-number-input')
   }
})
export default class RegisterFormComponent extends Vue {
  // prop reactive member variables publically available to the html template

  // public reactive member variables available to the html template
  public preFilledUser: StoryUser
  public REQUIRE_PASSWORD2 = true
  public firstName: string = null;
  public lastName: string = null;
  public email: string = null;
  public phone: string = null;
  public password: string = null;
  public password2: string = null;
  public fullPhoneNumber: string = null;
  public phoneNumberIsValid: boolean = null;
  public invitedByName: string = null
  public showPhoneField: boolean = false
  public showingOnlyEmailField: boolean = true    // start slow

  // public functions available to the html template
  public get loading(): boolean { return this.$store.state.loading }
  public get emailPlaceholder(): string { return "your email address" }

  public clearFields() {
    this.firstName = null;
    this.lastName = null;
    this.email = null;
    this.phone = null;
    this.password = null;
    this.password2 = null;
  }
  public onCancel() {
    this.$emit('close', false)
    this.clearFields()
  }

  // Parents call this to show modal (use a ref to call child -- https://stackoverflow.com/questions/42632711/how-to-call-function-on-child-component-on-parent-events)
  public async onShow(dynamicProps: { preFilledUser: StoryUser, phone: string, invitedByName: string, showPhoneField: boolean, email?: string  }) {
    if (!dynamicProps) return
    if (dynamicProps.preFilledUser) {
      this.preFilledUser = dynamicProps.preFilledUser

      this.firstName = dynamicProps.preFilledUser.firstName
      this.lastName = dynamicProps.preFilledUser.lastName
      this.email = dynamicProps.preFilledUser.email          // probably the only one guaranteed to be filled in, depending on our UI requirements at invite stage
      this.phone = dynamicProps.phone
      this.showPhoneField = dynamicProps.showPhoneField
    }
    this.invitedByName = dynamicProps.invitedByName
    if (this.preFilledUser) this.showingOnlyEmailField = false

    if (dynamicProps.email) {
      this.email = dynamicProps.email;
      await utils.sleep(100)    // or else the focus to firstNameField doesn't work
      this.onEmailEnterKey()
    }
  }

  public onEmailEnterKey() {
    if (this.showingOnlyEmailField) {
      if (!this.email || this.email.length === 0 || !utils.isValidEmail(this.email)) {
        utils.toast(this, 'Missing field', 'Please enter a valid email address.')
      } else {
        this.showingOnlyEmailField = false

        // @ts-ignore: because segment analytics is loaded in head script
        window.analytics.track('User EnteredEmail', {})

        this.$nextTick(() => ((this.$refs.firstNameField as Vue).$el as HTMLInputElement).focus())
      }
      return
    }
    ((this.$refs.firstNameField as Vue).$el as HTMLInputElement).focus()
  }

  public onPhoneUpdate(o: any) {
    this.fullPhoneNumber = o.e164
    this.phoneNumberIsValid = o.isValid
  }
  public async onOK() {
    if (this.showingOnlyEmailField) {
      this.onEmailEnterKey()
      return
    }

    // tidy up form fields
    if (this.firstName) this.firstName = utils.capitalizeFirstLetter(this.firstName.trim())
    if (this.lastName) this.lastName = utils.capitalizeFirstLetter(this.lastName.trim())
    if (this.email) this.email = this.email.trim()
    // if (this.phone) this.phone = utils.keepOnlyDigits(this.phone)      // no need to, because vue-tel-input has self-formatting and we don't even use this.phone (we use this.fullPhoneNumber instead)
    if (this.password) this.password = this.password.trim()
    if (this.password2) this.password2 = this.password2.trim()

    // validate form fields
    let abort: boolean = false
    if (!this.email || this.email.length === 0) {
      abort = true
      utils.toast(this, 'Missing field', 'Please enter an email address.')
    }
    if (this.email && !utils.isValidEmail(this.email)) {
      abort = true
      utils.toast(this, 'Invalid email', 'Please enter a valid email address.')
    }
    if (!utils.isValidName(this.firstName)) {
      abort = true
      utils.toast(this, 'Missing field', 'Please enter a first name.')
    }
    if (!utils.isValidName(this.lastName)) {
      abort = true
      utils.toast(this, 'Missing field', 'Please enter a last name.')
    }
    if (!this.password || this.password.length === 0) {
      abort = true
      utils.toast(this, 'Missing field', 'Please create a password.')
    } else if (!utils.isValidPassword(this.password)) {
      abort = true
      utils.toast(this, 'Invalid password', 'Your password must be at least 4 characters.')
    }
    if (this.REQUIRE_PASSWORD2) {
      if (!this.password2 || this.password2.length === 0) {
        abort = true
        utils.toast(this, 'Missing field', 'Password is required twice.')
      }
      if (this.password !== this.password2) {
        abort = true
        utils.toast(this, 'Invalid passwords', 'Passwords must match.')
      }
    }

    if (this.showPhoneField) {
      if (!this.fullPhoneNumber) {
        abort = true
        utils.toast(this, 'Missing field', 'Please enter a phone number.')
      } else if (!this.phoneNumberIsValid) {
        abort = true
        utils.toast(this, 'Invalid phone #', 'Your phone number is invalid.')
      }
    }

    if (abort) return

    // get utm parameters in case we have them captured by App, Home, HomeAnonymous, Blog or Lander.vue
    const utm_source: string = localStorage.getItem('utm_source')
    const utm_campaign: string = localStorage.getItem('utm_campaign')
    const utm_medium: string = localStorage.getItem('utm_medium')
    const utm_content: string = localStorage.getItem('utm_content')
    const utm_term: string = localStorage.getItem('utm_term')

    // make POST call to server
    try {
      this.$store.commit('setLoading', { loading: true });
      const url: string = utils.getURLprefix() + '/api/auth/register'
      const response: any = await utils.makePostCall(url, { firstName: this.firstName, lastName: this.lastName, email: this.email, phone: this.fullPhoneNumber, password: this.password, password2: this.password2, utm_source, utm_campaign, utm_medium, utm_content, utm_term } );
      if (!response || !response.data) throw(new Error('RegisterFormComponent.onOK() got null response or null response.data'))
      if (response.data.error) {
        this.$store.commit('setLoading', { loading: false });
        utils.toast(this, 'Error', response.data.error)
        // TODO: if error is "A user with that email already exists." then encourgage forgotton password flow
      }
      if (response.data.user) {
        // success
        this.$emit('close', true)

        const user: StoryUser = new StoryUser(response.data.user)
        utils_auth.saveAuthStuffToStoreAndLocalStorage(user, response.data.sessionId)

        // utils.showTopAlert("success", `Thanks for signing up! Check your email at ${this.email} for a confirmation`, 10)

        // @ts-ignore: because segment analytics is loaded in head script
        window.analytics.alias(user.getIdAsString())  // tie the previously anon user to this newly id'd user

        // @ts-ignore: because segment analytics is loaded in head script
        window.analytics.track('User Registered')

        // go to homepage - wrap in try/catch to avoid NavigationDuplicated error
        try { await this.$router.push({ name: 'home'}) } catch (Error) {}

        // refresh browser to start tutorial
        this.$store.commit('setInTutorial', { inTutorial: true });
        await this.$router.go(0)

        // clear out in case they do another
        this.clearFields()
      }

    } catch (error) {
      utils.toast(this, 'System Error', 'Could not register')
      if (this.$store.state.devmode) console.error(error);
    }

    this.$store.commit('setLoading', { loading: false });

  }

  // private, non-reactive member variables

  // private functions not available directly to HTML template
  private async mounted() { 
    if (this.$store.state.devmode) console.log(`${this.$options.name} mounted()`)

    // @ts-ignore: because segment analytics is loaded in head script
    window.analytics.page(this.$options.name)

    // @ts-ignore: because segment analytics is loaded in head script
    window.analytics.track('User ClickedSignup', {})

  }

  private async beforeDestroy() { if (this.$store.state.devmode) console.log(`${this.$options.name} beforeDestroy()`) }

}
